import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  HistoricalKpiResponse,
  Kpi,
  KpiChartDataResponse,
  KpiConfig,
  KpiKeys,
  KpisByKey
} from '@models';
import { CommonFunctionsService } from '@services';
import { environment, featureFlags } from 'environments/environment';
import { Observable, of, switchMap, timeout } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class KpiService {
  private http: HttpClient = inject(HttpClient);
  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);
  private BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;
  private readonly HISTORICAL_KPI_SKIP_KEYS = new Set<KpiKeys>([
    'key_milestones_on_track_to_baseline'
  ]);

  getKpi(studySk: string, kpiKey: KpiKeys, browserTimezone: string): Observable<Kpi> {
    const GET_KPI_URL = `${this.BASE_URL}/v1/kpi/`;
    return this.commonFunctionsService
      .getRequestCommonParams(['org_ids', 'parent_client_names'])
      .pipe(
        switchMap((commonParams) => {
          const params = commonParams
            .set('study_sk', studySk)
            .set('kpi', kpiKey)
            .set('browser_timezone', browserTimezone);
          return this.http.get<Kpi>(GET_KPI_URL, {
            withCredentials: true,
            params
          });
        })
      );
  }

  getKpiAll(studySk: string, kpiKey: KpiKeys[], browserTimezone: string): Observable<KpisByKey> {
    const GET_KPI_URL = `${this.BASE_URL}/v1/kpi/all`;

    return this.commonFunctionsService
      .getRequestCommonParams(['org_ids', 'parent_client_names'])
      .pipe(
        switchMap((commonParams) => {
          let params = commonParams.set('study_sk', studySk);
          params = params.append('browser_timezone', browserTimezone);
          // Filter out hidden KPIs based on environment configuration
          const filteredKpiKeys = kpiKey.filter((key) => !featureFlags.hiddenKPIs.includes(key));

          filteredKpiKeys.forEach((key) => {
            params = params.append('kpis', key);
          });
          return this.http.get<KpisByKey>(GET_KPI_URL, {
            withCredentials: true,
            params
          });
        })
      );
  }

  getHistoricalKpi(
    studySk: string,
    kpiKey: KpiKeys,
    browserTimezone: string
  ): Observable<HistoricalKpiResponse> {
    if (this.HISTORICAL_KPI_SKIP_KEYS.has(kpiKey)) {
      return of({ data: [] });
    }

    const TIMEOUT = environment.KPI_CHART_DATA_TIMEOUT;
    const GET_HISTORICAL_KPI_URL = `${this.BASE_URL}/v1/kpi/historical`;

    return this.commonFunctionsService
      .getRequestCommonParams(['org_ids', 'parent_client_names'])
      .pipe(
        switchMap((commonParams) => {
          const params = commonParams
            .set('study_sk', studySk)
            .set('kpi', kpiKey)
            .set('browser_timezone', browserTimezone);

          return this.http
            .get<HistoricalKpiResponse>(GET_HISTORICAL_KPI_URL, {
              withCredentials: true,
              params
            })
            .pipe(timeout(TIMEOUT)) as Observable<HistoricalKpiResponse>;
        })
      );
  }

  getKpiConfig(): Observable<KpiConfig> {
    const GET_KPI_CONFIG_URL = `${this.BASE_URL}/v1/kpi/config`;

    return this.http.get<KpiConfig>(GET_KPI_CONFIG_URL, {
      withCredentials: true
    });
  }

  getKpiChartData(studySk: string, kpiKey: KpiKeys): Observable<KpiChartDataResponse> {
    const TIMEOUT = environment.KPI_CHART_DATA_TIMEOUT;
    const GET_KPI_CHART_DATA_URL = `${this.BASE_URL}/v1/kpi/chart-data`;
    return this.commonFunctionsService
      .getRequestCommonParams(['org_ids', 'parent_client_names'])
      .pipe(
        switchMap((commonParams) => {
          const params = commonParams.set('study_sk', studySk).set('kpi', kpiKey);
          return this.http
            .get<KpiChartDataResponse>(GET_KPI_CHART_DATA_URL, {
              withCredentials: true,
              params
            })
            .pipe(timeout(TIMEOUT)) as Observable<KpiChartDataResponse>;
        })
      );
  }
}
